import {Duration} from 'luxon';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {formatDuration} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  MessageModel,
  MessageVisualStatusesEnum,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {preventDragStart} from '../../../../../helpers/dragAndDropHelpers';
import {computeRenderDelayedUpstreamWarning} from '../../../../../helpers/models/messageHelpers';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';

/*
 * Props.
 */

export interface MessageViewerDateProps {
  message: MessageModel;
  isInDragRegion?: boolean;
  timelineVariation: TimelineVariationsEnum;
}

interface StyledDateDivProps {
  $timelineVariation: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledDateDiv = styled.div<StyledDateDivProps>`
  color: ${(p) => p.theme.greys.shade70};
  line-height: 14px;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  text-transform: uppercase;
  grid-area: date;
  margin: 0 7px;

  ${(p) => addSynchronousStyles(p)}
`;

function addSynchronousStyles(props: StyledDateDivProps) {
  if (props.$timelineVariation !== TimelineVariationsEnum.SYNCHRONOUS) {
    return '';
  }

  // Based off the style in CommentBase
  return css`
    font-size: ${fontSizes.tiny};
    color: ${(p) => p.theme.greys.shade70};
    font-weight: ${fontWeights.normal};
  `;
}

const StyledDelayedUpstreamTooltip = styled(TooltipCoordinator)`
  color: ${(p) => p.theme.palette.orange.shade40};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;

/*
 * Component.
 */

export const MessageViewerDate: FC<MessageViewerDateProps> = ({
  message,
  isInDragRegion,
  timelineVariation,
}) => {
  // We do not show the date if there is no date or if this is a shared draft.
  if (message.sentAt <= 0 || message.visualStatus === MessageVisualStatusesEnum.DRAFT) {
    return null;
  }

  return (
    <StyledDateDiv
      draggable={isInDragRegion}
      onDragStart={preventDragStart}
      $timelineVariation={timelineVariation}
    >
      {renderMessageDateContent(message, timelineVariation)}
    </StyledDateDiv>
  );
};

function renderMessageDateContent(message: MessageModel, timelineVariation: TimelineVariationsEnum) {
  const relativeFormat =
    timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
      ? RelativeDateTimeFormatsEnum.SHORT
      : RelativeDateTimeFormatsEnum.LONG;

  const shouldRenderDelayedUpstreamWarning = computeRenderDelayedUpstreamWarning(message);

  if (!shouldRenderDelayedUpstreamWarning) {
    return <InteractiveDateTimeFormatter value={message.sentAt} relativeFormat={relativeFormat} />;
  }

  // We want to call out that the message was delivered late to Front
  // = there was a significant gap between when the message was sent and when it was received by our servers.
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDelayedUpstreamTooltip
          isMultiline
          render={() => {
            // Assume that message.createdAt is close enough to when it was actually received.
            // These can differ when we have a queue backlog, but the delayed_upstream flag is only set
            // when we know for sure that the message was delivered late.
            const delay = message.createdAt - message.sentAt;
            const duration = formatDuration(Duration.fromMillis(delay), {
              unitDisplay: 'long',
              maximumFractionDigits: 0,
            });

            return (
              <Trans id="message-delayed-upstream">
                This message was delivered to Front {duration} after it was sent.
              </Trans>
            );
          }}
          placement="bottom"
        >
          <Icon name="warningFilledTiny" color={theme.palette.orange.shade40} />
          <InteractiveDateTimeFormatter value={message.sentAt} relativeFormat={relativeFormat} />
        </StyledDelayedUpstreamTooltip>
      )}
    </UserThemeConsumer>
  );
}
